import { Injectable, inject, signal, computed, Signal } from '@angular/core';
import { Observable, BehaviorSubject, map, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { Employee, EmployeeListItem, EmployeeSearchParams, ManagerOption } from '../shared/models/employee.model';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private readonly apiService = inject(ApiService);
  
  // Signals for reactive state management
  private _employees = signal<Employee[]>([]);
  private _loading = signal<boolean>(false);
  private _error = signal<string | null>(null);
  private _searchParams = signal<EmployeeSearchParams>({
    searchTerm: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // Public readonly signals
  readonly employees = this._employees.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly searchParams = this._searchParams.asReadonly();

  // Computed signal for filtered and sorted employee list
  readonly filteredEmployees = computed(() => {
    const employees = this._employees();
    const params = this._searchParams();
    
    let filtered = [...employees];

    // Apply search filter
    if (params.searchTerm) {
      const searchTerm = params.searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.personalDetails.firstName.toLowerCase().includes(searchTerm) ||
        emp.personalDetails.lastName.toLowerCase().includes(searchTerm) ||
        emp.personalDetails.email.toLowerCase().includes(searchTerm) ||
        emp.jobDetails.jobTitle.toLowerCase().includes(searchTerm) ||
        emp.jobDetails.department.toLowerCase().includes(searchTerm) ||
        emp.jobDetails.employeeNumber?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply department filter
    if (params.department) {
      filtered = filtered.filter(emp => emp.jobDetails.department === params.department);
    }

    // Apply status filter
    if (params.status) {
      filtered = filtered.filter(emp => emp.jobDetails.employeeStatus === params.status);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (params.sortBy) {
        case 'name':
          aValue = `${a.personalDetails.firstName} ${a.personalDetails.lastName}`;
          bValue = `${b.personalDetails.firstName} ${b.personalDetails.lastName}`;
          break;
        case 'role':
          aValue = a.jobDetails.jobTitle;
          bValue = b.jobDetails.jobTitle;
          break;
        case 'department':
          aValue = a.jobDetails.department;
          bValue = b.jobDetails.department;
          break;
        case 'status':
          aValue = a.jobDetails.employeeStatus;
          bValue = b.jobDetails.employeeStatus;
          break;
        default:
          aValue = `${a.personalDetails.firstName} ${a.personalDetails.lastName}`;
          bValue = `${b.personalDetails.firstName} ${b.personalDetails.lastName}`;
      }

      const comparison = aValue.localeCompare(bValue);
      return params.sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  });

  // Computed signal for employee list items (simplified view)
  readonly employeeListItems = computed((): EmployeeListItem[] => {
    return this.filteredEmployees().map(emp => ({
      id: emp.id,
      fullName: `${emp.personalDetails.firstName} ${emp.personalDetails.lastName}`,
      profileImage: emp.personalDetails.profileImage,
      role: emp.jobDetails.jobTitle,
      department: emp.jobDetails.department,
      status: emp.jobDetails.employeeStatus,
      email: emp.personalDetails.email,
      employeeNumber: emp.jobDetails.employeeNumber,
      managerId: emp.jobDetails.managerId,
      managerName: emp.jobDetails.managerName
    }));
  });

  // Load all employees
  loadEmployees(): void {
    this._loading.set(true);
    this._error.set(null);

    this.apiService.get<Employee[]>('api/employees').subscribe({
      next: (employees) => {
        this._employees.set(employees);
        this._loading.set(false);
      },
      error: (error) => {
        this._error.set('Failed to load employees');
        this._loading.set(false);
        console.error('Error loading employees:', error);
      }
    });
  }

  // Get employee by ID
  getEmployeeById(id: string): Observable<Employee | null> {
    return this.apiService.get<Employee>(`api/employees/${id}`).pipe(
      catchError(error => {
        console.error('Error loading employee:', error);
        return of(null);
      })
    );
  }

  getAllEmployees(): Signal<Employee[]> {
    this.loadEmployees();
    return this._employees.asReadonly();
  }

  // Update search parameters
  updateSearchParams(params: Partial<EmployeeSearchParams>): void {
    this._searchParams.update(current => ({ ...current, ...params }));
  }

  // Clear search
  clearSearch(): void {
    this._searchParams.update(current => ({ ...current, searchTerm: '' }));
  }

  // Toggle sort order
  toggleSortOrder(): void {
    this._searchParams.update(current => ({
      ...current,
      sortOrder: current.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  }

  // Set sort by field
  setSortBy(field: 'name' | 'role' | 'department'): void {
    this._searchParams.update(current => ({ ...current, sortBy: field }));
  }

  // Create new employee
  createEmployee(employee: Omit<Employee, 'id'>): Observable<Employee> {
    return this.apiService.post<Employee>('api/employees', employee);
  }

  // Update employee
  updateEmployee(id: string, employee: Partial<Employee>): Observable<Employee> {
    return this.apiService.put<Employee>(`api/employees/${id}`, employee);
  }

  // Delete employee
  deleteEmployee(id: string): Observable<void> {
    return this.apiService.delete<void>(`api/employees/${id}`);
  }

  // Get unique departments for filtering
  readonly departments = computed(() => {
    const employees = this._employees();
    const depts = new Set(employees.map(emp => emp.jobDetails.department));
    return Array.from(depts).sort();
  });

  // Get unique statuses for filtering
  readonly statuses = computed(() => {
    const employees = this._employees();
    const statuses = new Set(employees.map(emp => emp.jobDetails.employeeStatus));
    return Array.from(statuses).sort();
  });

  // Get unique employment types for filtering
  readonly employmentTypes = computed(() => {
    const employees = this._employees();
    const types = new Set(employees.map(emp => emp.jobDetails.employmentType));
    return Array.from(types).sort();
  });

  // Get potential managers
  getPotentialManagers(): Observable<ManagerOption[]> {
    return this.apiService.get<Employee[]>('api/employees/managers').pipe(
      map(employees => employees.map(emp => ({
        id: emp.id,
        name: `${emp.personalDetails.firstName} ${emp.personalDetails.lastName}`,
        jobTitle: emp.jobDetails.jobTitle,
        department: emp.jobDetails.department,
        profileImage: emp.personalDetails.profileImage
      }))),
      catchError(error => {
        console.error('Error loading managers:', error);
        return of([]);
      })
    );
  }
}
